<template>
  <div
    class="event-carousel event-carousel-tagging"
    :class="{ 'event-carousel--empty': events.length == 0 }"
    :style="containerStyle"
  >
    <div class="event-carousel__header">
      <div class="event-carousel__title" :style="isMobile ? { fontSize: carouselTitleSize + 'px' } : null">{{title}}</div>
      <div class="event-carousel__filters">
        <button class="event-carousel__filters-toggle event-carousel__filters--modal-element" type="button" @click="toggleFilters()">
          <svg class="event-carousel__filters-toggle--icon">
            <use xlink:href="#icon-slider" />
          </svg>
          {{filters.modalBtn}}
        </button>
        <TransitionGroup
          name="event-carousel__filters--wrapper"
          class="event-carousel__filters--wrapper"
          :class="{ 'open': showFilters }"
        >
          <div key="backdrop" class="event-carousel__filters--backdrop event-carousel__filters--modal-element" v-if="filterModalIsOpen" @click.stop="toggleFilters(false)"></div>
          <div key="container" class="event-carousel__filters--container" v-if="showFilters">
            <div class="event-carousel__filters--title event-carousel__filters--modal-element">
              <c-text weight="bolder" size="lg"> {{filters.modalTitle}} </c-text>
              <button class="event-carousel__filters--close" type="button" @click="toggleFilters(false)">
                <svg>
                  <use xlink:href="#close" />
                </svg>
              </button>
            </div>
            <event-dropdown
              :label="filters.period.label"
              :options="periodFilters"
              v-model="period"
              v-if="filters.period.show"
            />
            <event-dropdown
              :label="filters.type.label"
              :options="typeFilters"
              v-model="type"
              v-if="filters.type.show"
            />
            <event-dropdown
              :label="filters.location.label"
              :options="locationFilters"
              v-model="location"
              v-if="filters.location.show"
            />
            <event-dropdown
              :label="filters.category.label"
              :options="categoryFilters"
              v-model="category"
              v-if="filters.category.show"
            />
          </div>
        </TransitionGroup>
      </div>
    </div>
    <div
      class="event-carousel__navigation-wrapper"
      v-show="filteredEvents.length > 0"
    >
      <button
        class="event-carousel__navigation event-carousel__navigation--prev event-carousel-button--disabled"
        type="button"
        name="Show Previous Events"
        ref="eventNavPrev"
        :style="navigationStyle"
        @click="changeFocus(-1)"
      >
        <svg class="event-carousel__navigation--icon">
          <use xlink:href="#chevron-left" />
        </svg>
      </button>
      <button
        class="event-carousel__navigation event-carousel__navigation--next"
        type="button"
        name="Show Next Events"
        ref="eventNavNext"
        :style="navigationStyle"
        @click="changeFocus(1)"
      >
        <svg class="event-carousel__navigation--icon">
          <use xlink:href="#chevron-right" />
        </svg>
      </button>
      <div class="event-carousel__wrapper" :id="wrapperId" ref="wrapper">
        <div class="event-carousel__slider">
          <event-card
            v-for="event in filteredEvents"
            :key="event.id"
            :id="event.id"
            :button-label="event.buttonLabel"
            :title="event.title"
            :category="event.category"
            :location="event.location"
            :type="event.type"
            :link="event.link"
            :start-date="event.startDate"
            :end-date="event.endDate"
            :bg-color="cardBg"
            :btn-bg="btnBg"
            :btn-color="btnColor"
            :date-color="dateColor"
            :title-color="cardTitleColor"
            :category-color-map="categoryColorMap"
          />
          <div class="event-carousel__spacer"></div>
        </div>
      </div>
    </div>
    <div class="event-carousel__empty-state" v-if="events.length == 0">
      <p class="event-carousel__empty-title">{{emptyTitle}}</p>
      <p class="event-carousel__empty-text">
        {{emptyMsg}}
      </p>
      <div class="event-carousel__socials">
        <a href="#" class="event-carousel__social">
          <svg class="event-carousel__social--icon">
            <use xlink:href="#icon-twitter" />
          </svg>
        </a>
        <a href="#" class="event-carousel__social">
          <svg class="event-carousel__social--icon">
            <use xlink:href="#icon-linkedin" />
          </svg>
        </a>
        <a href="#" class="event-carousel__social">
          <svg class="event-carousel__social--icon">
            <use xlink:href="#icon-instagram" />
          </svg>
        </a>
        <a href="#" class="event-carousel__social">
          <svg class="event-carousel__social--icon">
            <use xlink:href="#icon-rg-social" />
          </svg>
        </a>
      </div>
    </div>
    <div
      class="event-carousel__empty-state"
      v-else-if="filteredEvents.length == 0"
    >
      <p class="event-carousel__empty-title">
        <svg class="event-carousel__inline-icon mr-3">
          <use xlink:href="#search" />
        </svg>
        {{emptyResultsTitle}}
      </p>
      <p class="event-carousel__empty-text">
        {{emptyResultsMsg}}
      </p>
    </div>
  </div>
</template>

<style lang="css" scoped>
  .event-carousel__navigation {
    z-index: 1;
  }
 .event-carousel-button--disabled {
    opacity: .35;
    cursor: auto;
  }
</style>

<script>
  import utils from '../../../utils/utils'

  export default {
    name: 'EventCarousel',
    props: {
      title: { type: String, default: 'Upcoming Events' },
      emptyTitle: { type: String, default: 'Coming soon' },
      emptyMsg: { type: String, default: 'The Niobium Events are coming soon, stay tuned on our social media to receive uptades.' },
      emptyResultsTitle: { type: String, default: 'No events found' },
      emptyResultsMsg: { type: String, default: 'Seems like we don\'t have any event based on your search.' },
      bgColor: { type: String },
      textColor: { type: String },
      cardBg: { type: String },
      btnBg: { type: String },
      btnColor: { type: String },
      dateColor: { type: String },
      cardTitleColor: { type: String },
      navigationColor: { type: String },
      navigationBg: { type: String },
      categoryColorMap: { type: Object, default: () => ({}) },
      scrollDuration: { type: Number, default: 200 },
      events: { type: Array, required: true, default: () => [] },
      filterConfig: { type: Object, default: () => ({}) },
      carouselTitleSize: { type: String, default: '28' },
      carouselPaddingBottom: { type: String, default: '0' }
    },
    data() {
      return {
        isMobile: window.innerWidth < 768,
        focus: 0,
        viewportWidth: window.innerWidth,
        location: '',
        type: '',
        category: '',
        period: '',
        filterModalIsOpen: false,
        filters: {
          modalTitle: this.filterConfig.modalTitle || 'Filter Events',
          modalBtn: this.filterConfig.modalBtn || 'Filter',
          period: {
            label: 'Period',
            show: true,
            options: []
          },
          location: {
            label: 'Location',
            show: true,
            options: []
          },
          type: {
            label: 'Type',
            show: true,
            options: []
          },
          category: {
            label: 'Category',
            show: true,
            options: []
          }
        }
      }
    },
    computed: {
      baseId() {
        return Math.floor(Math.random() * 1000)
      },
      wrapperId() {
        return `carousel-${this.baseId}-wrapper`
      },
      scrollOffset() {
        return this.viewportWidth >= 992 ? -70 : -18
      },
      showFilters() {
        return this.viewportWidth >= 768 ? true : this.filterModalIsOpen
      },
      taggedEvents() {
        return this.events.map((x, idx) => ({
          id: `carousel-${this.baseId}-event-${idx}`,
          ...x,
        }))
      },
      filteredEvents() {
        setTimeout(() => {
          this.slideTo(0).finally(() => {
            this.focus = 0
          })
        }, 0)

        let events = [...this.taggedEvents]
        events = filterByAttr(events, 'category', this.category)
        events = filterByAttr(events, 'type', this.type)
        events = filterByAttr(events, 'continent', this.location)
        events = filterByPeriod(events, this.period)
        events = events.map(event => {
          event.endDate = event.endDate === '0001-01-01' ? '' : event.endDate
          return event
        })
        return events.sort((a, b) => {
          if (a.startDate < b.startDate) return -1
          else if (a.startDate > b.startDate) return 1
          return 0
        })
      },
      containerStyle() {
        return {
          color: this.textColor,
          backgroundColor: this.bgColor,
          paddingBottom: `${this.carouselPaddingBottom}px`
        }
      },
      navigationStyle() {
        return {
          fill: this.navigationColor,
          backgroundColor: this.navigationBg
        }
      },
      periodFilters() {
        return this.filters.period.options.filter(opt => opt.show)
      },
      locationFilters() {
        return this.filters.location.options.filter(opt => opt.show)
      },
      typeFilters() {
        return this.filters.type.options.filter(opt => opt.show)
      },
      categoryFilters() {
        return this.filters.category.options.filter(opt => opt.show)
      },
    },
    methods: {
      handleViewportUpdate() {
        clearTimeout(evtHolder.viewportUpdatedTimeout)
        evtHolder.viewportUpdatedTimeout = setTimeout(() => {
          this.registerViewportDimensions()
          setTimeout(() => {
            this.slideTo(this.focus)
            if (this.viewportWidth < 768) this.filterModalIsOpen = false
          }, 10)
        }, 500)
      },
      registerViewportDimensions() {
        this.viewportWidth = window.innerWidth
      },
      scrollNumber(totalEvents) {
        const lgScreen = 992
        const mdScreen = 768
        let cardsOnScreen = 3

        if (this.getScreenWidth() > lgScreen) {
          cardsOnScreen = 3
        }
        else if (this.getScreenWidth() >= mdScreen && this.getScreenWidth() <= lgScreen) {
          cardsOnScreen = 2
        }
        else {
          cardsOnScreen = 1
        }

        return totalEvents - cardsOnScreen
      },
      changeFocus(dir) {
        let nextIdx = this.focus + dir
        const totalEvents = this.filteredEvents.length
        const arrowPrev = this.$refs.eventNavPrev
        const arrowNext = this.$refs.eventNavNext

        if (nextIdx < 0) nextIdx = 0
        else if (nextIdx >= this.filteredEvents.length) nextIdx = this.filteredEvents.length - 1
        if (nextIdx === this.focus) {
          return
        }

        this.slideTo(nextIdx).then((data) => {
          if (nextIdx === 0) {
            arrowNext.classList.remove('event-carousel-button--disabled')
            arrowPrev.classList.add('event-carousel-button--disabled')
          }
          else if (nextIdx >= this.scrollNumber(totalEvents)) {
            arrowPrev.classList.remove('event-carousel-button--disabled')
            arrowNext.classList.add('event-carousel-button--disabled')
          }
          else {
            arrowNext.classList.remove('event-carousel-button--disabled')
            arrowPrev.classList.remove('event-carousel-button--disabled')
          }

          if (data.moved) {
            this.focus = data.idx
          }
        })
      },
      getScreenWidth() {
        return window.innerWidth
      },
      slideTo(idx) {
        if (this.filteredEvents.length === 0) {
          return new Promise((resolve) => resolve({ idx, moved: false }))
        }
        const self = this
        return new Promise((resolve) => {
          const initialScrollY = self.$refs.wrapper.scrollLeft
          const nextId = `#${this.filteredEvents[idx < 0 ? 0 : idx].id}`
          evtHolder.scrollEvt = this.$scrollTo(nextId, this.scrollDuration, {
            container: `#${self.wrapperId}`,
            x: true,
            y: false,
            offset: self.scrollOffset,
            onDone: () => {
              const finalScrollY = self.$refs.wrapper.scrollLeft
              resolve({ idx, moved: finalScrollY !== initialScrollY })
            },
          })
        })
      },
      toggleFilters(value) {
        if (value !== undefined) this.filterModalIsOpen = value
        else this.filterModalIsOpen = !this.filterModalIsOpen
      },
    },
    mounted() {
      window.addEventListener('resize', this.handleViewportUpdate)
      setupFilterConfig(this.filters.period, this.filterConfig.period)
      setupFilterConfig(this.filters.location, this.filterConfig.location)
      setupFilterConfig(this.filters.category, this.filterConfig.category)
      setupFilterConfig(this.filters.type, this.filterConfig.type)

      if (utils.isMobile()) return
      utils.setAllElSameHeigts('event-card__title')
    },
    beforeDestroy() {
      window.removeEventListener('resize', this.handleViewportUpdate)
    }
  }

  const evtHolder = {
    viewportUpdatedTimeout: null,
    scrollEvt: null,
  }

  const setupFilterConfig = (filter, config) => {
    if (!config) {
      return
    }
    filter.label = config.label || filter.label
    filter.show = typeof (config.show) === 'boolean' ? config.show : filter.show
    if (config.options) {
      config.options.forEach(opt => {
        // const existing = filter.options.find(x => x.value === opt.id)
        // if (!existing) {
        //   return
        // }
        // existing.label = opt.label || existing.label
        // existing.show = typeof (opt.show) === 'boolean' ? opt.show : existing.show

        filter.options.push(opt)
      })
    }
  }

  const filterByAttr = (arr, field, filter) => {
    if (!filter || filter === 'all') return arr
    return arr.filter(x => {
      if (Array.isArray(x[field])) return x[field].find(y => y === filter)
      return x[field] === filter
    })
  }

  const filterByPeriod = (arr, filter) => {
    if (filter && filter !== 'all') {
      let start
      let end
      if (filter === 'thismonth') {
        [start, end] = periodinterval()
      } else if (filter === 'nextmonth') {
        [start, end] = periodinterval(1)
      } else if (filter === 'pastmonth') {
        [start, end] = periodinterval(-1)
      } else if (filter === 'past3months') {
        [start, end] = periodinterval(-3, 3)
      }
      return arr.filter((x) => x.startDate >= start && x.startDate <= end)
    }
    return arr
  }

  const periodinterval = (offset = 0, periodSize = 1) => {
    const now = new Date()
    now.setDate(1)
    if (offset !== 0) now.setMonth(now.getMonth() + offset)

    const start = yyyymmdd(now)
    now.setMonth(now.getMonth() + periodSize)
    now.setDate(now.getDate() - 1)
    const end = yyyymmdd(now)

    return [start, end]
  }

  const yyyymmdd = (date) =>
    `${date.getFullYear()}-${twoDigits(date.getMonth() + 1)}-${twoDigits(
      date.getDate()
    )}`

  const twoDigits = (number) => `0${number}`.slice(-2)
</script>
